/**
 * Mutation hooks for list member operations (invite, accept, reject, remove)
 *
 * Provides React Query mutations wrapping the list members API endpoints.
 * Each mutation invalidates relevant query keys on success.
 */

import * as listsApi from '@/api/endpoints/lists';
import { LISTS_QUERY_KEYS } from '@/api/queries/lists';
import type { ListMemberCreateInput } from '@/api/schemas/list';
import { useMutation, useQueryClient } from '@tanstack/react-query';

/**
 * Hook for inviting a user to a list as a member
 *
 * On success, invalidates the members query for the list.
 */
export function useInviteListMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, data }: { listId: string; data: ListMemberCreateInput }) =>
      listsApi.inviteListMember(listId, data),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.members(variables.listId) });
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.list(variables.listId) });
    },
    onError: (error) => {
      console.error('[useInviteListMember] Failed to invite member:', error);
    },
  });
}

/**
 * Hook for accepting a list invitation
 *
 * On success, invalidates members and lists (new list appears in user's lists).
 */
export function useAcceptListInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, memberId }: { listId: string; memberId: string }) =>
      listsApi.acceptListInvitation(listId, memberId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.members(variables.listId) });
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.lists() });
    },
    onError: (error) => {
      console.error('[useAcceptListInvitation] Failed to accept invitation:', error);
    },
  });
}

/**
 * Hook for rejecting a list invitation
 *
 * On success, invalidates members and lists.
 */
export function useRejectListInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, memberId }: { listId: string; memberId: string }) =>
      listsApi.rejectListInvitation(listId, memberId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.members(variables.listId) });
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.lists() });
    },
    onError: (error) => {
      console.error('[useRejectListInvitation] Failed to reject invitation:', error);
    },
  });
}

/**
 * Hook for responding to a list invitation (accept/decline)
 * Uses the convenience endpoint that resolves membership from JWT.
 *
 * On success, invalidates list detail and lists queries.
 */
export function useRespondToListInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, action }: { listId: string; action: 'accept' | 'decline' }) =>
      listsApi.respondToListInvitation(listId, action),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.list(variables.listId) });
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.lists() });
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.members(variables.listId) });
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.items(variables.listId) });
    },
    onError: (error) => {
      console.error('[useRespondToListInvitation] Failed to respond to invitation:', error);
    },
  });
}

/**
 * Hook for removing a member from a list (owner action)
 *
 * On success, invalidates the members query for the list.
 */
export function useRemoveListMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ listId, memberId }: { listId: string; memberId: string }) =>
      listsApi.removeListMember(listId, memberId),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.members(variables.listId) });
      queryClient.invalidateQueries({ queryKey: LISTS_QUERY_KEYS.list(variables.listId) });
    },
    onError: (error) => {
      console.error('[useRemoveListMember] Failed to remove member:', error);
    },
  });
}
